<template>
  <div id="CramPacks" class="fontist">
    <!-- Hero -->
    <div class="cram-hero">
      <b-container>
        <h1 class="cram-hero__title">AP World History Cram Packs</h1>
        <p class="cram-hero__subtitle">
          Download concise unit-based review packets to help you study key
          AP&nbsp;World History content quickly before your exam.
        </p>
      </b-container>
    </div>

    <!-- Unit cards -->
    <b-container class="cram-grid-section">
      <b-row class="cram-grid" cols="1" cols-sm="2" cols-lg="3">
        <b-col
          v-for="unit in units"
          :key="unit.number"
          class="cram-col d-flex"
        >
          <div
            class="cram-card"
            :class="{ 'cram-card--available': unit.available }"
          >
            <div class="cram-card__header">
              <span class="cram-card__unit-label">Unit {{ unit.number }}</span>
              <span
                class="cram-badge"
                :class="
                  unit.available ? 'cram-badge--available' : 'cram-badge--soon'
                "
              >
                {{ unit.available ? "Available" : "Coming Soon" }}
              </span>
            </div>

            <h2 class="cram-card__title">{{ unit.title }}</h2>

            <div class="cram-card__footer">
              <a
                v-if="unit.available"
                :href="unit.downloadUrl"
                target="_blank"
                rel="noopener noreferrer"
                class="cram-btn cram-btn--download"
                @click="handleDownloadClick(unit)"
              >
                <svg
                  class="cram-btn__icon"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                  aria-hidden="true"
                >
                  <path
                    fill-rule="evenodd"
                    d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z"
                    clip-rule="evenodd"
                  />
                </svg>
                Download Cram Pack
              </a>
              <button v-else class="cram-btn cram-btn--soon" disabled>
                Not Yet Available
              </button>
            </div>
          </div>
        </b-col>
      </b-row>
    </b-container>
  </div>
</template>

<script>
import { onMounted } from "vue";
import { trackEvent } from "@/services/analyticsService";

export default {
  name: "CramPacksView",
  setup() {
    // ─── Download URLs ────────────────────────────────────────────────────────
    // Replace these placeholder values with the real file URLs when ready.
    const UNIT_1_URL = "https://firebasestorage.googleapis.com/v0/b/ap-studybites.appspot.com/o/Unit%201%20Global%20Tapestry%20Cram%20Pack%20(A4).pdf?alt=media&token=7cd980e9-3b7e-4b79-b1eb-7963ba5617ac";
    const UNIT_7_URL = "https://firebasestorage.googleapis.com/v0/b/ap-studybites.appspot.com/o/Unit%207%20Global%20Conflict%20Cram%20Pack%20(A4).pdf?alt=media&token=436cb889-f59a-48a0-ac32-b0e84798de85";
    // ─────────────────────────────────────────────────────────────────────────

    const units = [
      { number: 1, title: "The Global Tapestry",               available: true,  downloadUrl: UNIT_1_URL },
      { number: 2, title: "Networks of Exchange",              available: false, downloadUrl: null },
      { number: 3, title: "Land-Based Empires",                available: false, downloadUrl: null },
      { number: 4, title: "Transoceanic Interconnections",     available: false, downloadUrl: null },
      { number: 5, title: "Revolutions",                       available: false, downloadUrl: null },
      { number: 6, title: "Consequences of Industrialization", available: false, downloadUrl: null },
      { number: 7, title: "Global Conflict",                   available: true,  downloadUrl: UNIT_7_URL },
      { number: 8, title: "Cold War & Decolonization",         available: false, downloadUrl: null },
      { number: 9, title: "Globalization",                     available: false, downloadUrl: null },
    ];

    const handleDownloadClick = unit => {
      trackEvent("cram_pack_downloaded", {
        unit_number: unit.number,
        unit_title: unit.title
      });
    };

    onMounted(() => {
      trackEvent("cram_pack_viewed");
    });

    return { units, handleDownloadClick };
  }
};
</script>

<style scoped>
/* ── Layout ─────────────────────────────────────────────────────────────── */
#CramPacks {
  min-height: 100vh;
  background: #fafafa;
}

/* ── Hero ────────────────────────────────────────────────────────────────── */
.cram-hero {
  background: #fff;
  border-bottom: 1px solid #ebebeb;
  padding: 56px 0 48px;
  text-align: center;
}

.cram-hero__title {
  font-family: "Manrope", sans-serif;
  font-size: 2rem;
  font-weight: 800;
  color: #18191f;
  margin-bottom: 12px;
}

.cram-hero__subtitle {
  font-family: "Manrope", sans-serif;
  font-size: 1rem;
  font-weight: 400;
  color: #555;
  max-width: 560px;
  margin: 0 auto;
  line-height: 1.6;
}

/* ── Grid ────────────────────────────────────────────────────────────────── */
.cram-grid-section {
  padding: 48px 16px 80px;
}

.cram-grid {
  row-gap: 24px;
}

.cram-col {
  padding: 0 12px;
}

/* ── Card ────────────────────────────────────────────────────────────────── */
.cram-card {
  background: #fff;
  border: 1.5px solid #ebebeb;
  border-radius: 14px;
  padding: 24px 22px 20px;
  display: flex;
  flex-direction: column;
  width: 100%;
  transition: box-shadow 0.18s ease, transform 0.18s ease;
  opacity: 0.72;
}

.cram-card:hover {
  box-shadow: 0 4px 18px rgba(0, 0, 0, 0.08);
  transform: translateY(-2px);
}

/* Available cards get full opacity and a subtle purple accent */
.cram-card--available {
  opacity: 1;
  border-color: #e4d2ff;
}

.cram-card--available:hover {
  box-shadow: 0 4px 22px rgba(140, 48, 245, 0.12);
}

/* ── Card header row ─────────────────────────────────────────────────────── */
.cram-card__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 10px;
}

.cram-card__unit-label {
  font-family: "Manrope", sans-serif;
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #8c30f5;
}

/* ── Badge ───────────────────────────────────────────────────────────────── */
.cram-badge {
  display: inline-block;
  border-radius: 999px;
  font-family: "Manrope", sans-serif;
  font-size: 0.7rem;
  font-weight: 700;
  padding: 3px 10px;
}

.cram-badge--available {
  background: #f0e6ff;
  color: #6a0fd6;
}

.cram-badge--soon {
  background: #f2f2f2;
  color: #999;
}

/* ── Card title ──────────────────────────────────────────────────────────── */
.cram-card__title {
  font-family: "Manrope", sans-serif;
  font-size: 1rem;
  font-weight: 700;
  color: #18191f;
  margin: 0 0 20px;
  line-height: 1.4;
  flex: 1;
}

/* ── Footer / button row ─────────────────────────────────────────────────── */
.cram-card__footer {
  margin-top: auto;
}

/* ── Buttons ─────────────────────────────────────────────────────────────── */
.cram-btn {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  border-radius: 8px;
  font-family: "Manrope", sans-serif;
  font-size: 0.875rem;
  font-weight: 700;
  padding: 9px 18px;
  cursor: pointer;
  border: none;
  text-decoration: none;
  transition: background 0.16s ease, color 0.16s ease;
  width: 100%;
  justify-content: center;
}

.cram-btn--download {
  background: #8c30f5;
  color: #fff;
}

.cram-btn--download:hover {
  background: #7020d8;
  color: #fff;
  text-decoration: none;
}

.cram-btn--soon {
  background: #f2f2f2;
  color: #aaa;
  cursor: not-allowed;
}

.cram-btn__icon {
  width: 16px;
  height: 16px;
  flex-shrink: 0;
}

/* ── Responsive tweaks ───────────────────────────────────────────────────── */
@media (max-width: 576px) {
  .cram-hero {
    padding: 40px 0 32px;
  }

  .cram-hero__title {
    font-size: 1.5rem;
  }

  .cram-grid-section {
    padding: 32px 8px 60px;
  }
}
</style>
